import { useParams, Link } from 'react-router-dom';
import { articles, categories } from '@/mocks/siteData';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import SEOHead from '@/components/feature/SEOHead';
import Breadcrumb from '@/components/base/Breadcrumb';
import NotFound from '@/pages/NotFound';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();

  const article = articles.find((a) => a.slug === slug);
  if (!article) return <NotFound />;

  const category = categories.find((c) => c.slug === article.category);
  const relatedArticles = articles
    .filter((a) => a.slug !== article.slug && a.category === article.category)
    .slice(0, 3);

  const categoryNames: Record<string, string> = {
    shouban: '手办周边',
    fuzhuang: 'JK服装',
    tongren: '同人文化',
    gouwu: '购物指南',
  };

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.summary,
    author: { '@type': 'Person', name: article.author },
    datePublished: article.date,
    url: `https://shiroi-jk.com/article/${slug}`,
    inLanguage: 'zh-CN',
    publisher: {
      '@type': 'Organization',
      name: '白丝JK正版周边购买指南',
      url: 'https://shiroi-jk.com',
    },
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      <SEOHead
        title={article.title}
        description={article.summary}
        keywords={article.tags.join(',')}
        canonicalUrl={`https://shiroi-jk.com/article/${slug}`}
        ogType="article"
        ogImage={article.coverImage}
      />
      <ScrollToTop />
      <Navbar />
      <main className="pt-16">
        <div className="max-w-4xl mx-auto px-4 md:px-6 py-8">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              ...(category ? [{ label: category.name, href: `/category/${category.slug}` }] : []),
              { label: article.title },
            ]}
          />
        </div>

        <article>
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <div className="aspect-[21/9] rounded-2xl overflow-hidden mb-8">
              <img
                src={article.coverImage}
                alt={article.title}
                className="w-full h-full object-cover"
              />
            </div>

            <div className="flex flex-wrap items-center gap-3 mb-4">
              {category && (
                <Link
                  to={`/category/${category.slug}`}
                  className="px-3 py-1 rounded-full bg-secondary-100 text-secondary-700 text-xs font-medium cursor-pointer hover:bg-secondary-200 transition-colors"
                >
                  {categoryNames[article.category] || article.category}
                </Link>
              )}
              <span className="text-xs text-foreground-400">{article.date}</span>
              <span className="text-xs text-foreground-400">{article.author}</span>
            </div>

            <h1 className="font-heading text-2xl md:text-4xl text-foreground-950 mb-6 leading-tight">
              {article.title}
            </h1>

            <div className="flex flex-wrap items-center gap-2 mb-10">
              {article.tags.map((tag) => (
                <span key={tag} className="text-xs text-foreground-500 bg-background-100 px-2.5 py-1 rounded-full">
                  #{tag}
                </span>
              ))}
            </div>

            <div
              className="article-content pb-10 border-b border-background-200/70"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />
          </div>
        </article>

        <section className="max-w-4xl mx-auto px-4 md:px-6 py-12">
          <h2 className="font-heading text-2xl text-foreground-950 mb-8">
            相关文章推荐
          </h2>
          {relatedArticles.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
              {relatedArticles.map((related) => (
                <Link
                  key={related.slug}
                  to={`/article/${related.slug}`}
                  className="group bg-background-50 rounded-xl overflow-hidden transition-all duration-300 cursor-pointer hover:-translate-y-1 border border-background-200/70"
                >
                  <div className="aspect-[16/9] overflow-hidden">
                    <img
                      src={related.coverImage}
                      alt={related.title}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                  </div>
                  <div className="p-4">
                    <h3 className="font-heading text-sm text-foreground-900 line-clamp-2 group-hover:text-primary-500 transition-colors duration-200">
                      {related.title}
                    </h3>
                    <p className="text-xs text-foreground-400 mt-2">{related.date}</p>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <p className="text-sm text-foreground-400">暂无相关推荐</p>
          )}
        </section>
      </main>
      <Footer />
    </>
  );
}